if (!customElements.get("product-perfect-match")) {
  class ProductPerfectMatch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.tracks = Array.from(this.querySelectorAll(".product-perfect-match__grid"));
      this.states = new WeakMap();

      this.onResize = this.onResize.bind(this);
      this.onTabClick = this.onTabClick.bind(this);
      this.onArrowClick = this.onArrowClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.querySelectorAll(".product-perfect-match__tab").forEach((button) => {
        button.addEventListener("click", this.onTabClick);
      });

      this.tracks.forEach((track) => {
        const panel = track.closest(".product-perfect-match__panel");
        const previousButton = panel?.querySelector(
          ".product-perfect-match__arrow--previous",
        );
        const nextButton = panel?.querySelector(".product-perfect-match__arrow--next");

        this.states.set(track, {
          activePointerId: null,
          dragging: false,
          hasMoved: false,
          nextButton,
          previousButton,
          startScrollLeft: 0,
          startX: 0,
          suppressClick: false,
        });

        track.addEventListener("pointerdown", this.onPointerDown);
        track.addEventListener("pointermove", this.onPointerMove);
        track.addEventListener("pointerup", this.onPointerUp);
        track.addEventListener("pointercancel", this.onPointerUp);
        track.addEventListener("click", this.onClick, true);
        track.addEventListener("dragstart", this.onDragStart);
        track.addEventListener("scroll", this.onScroll, { passive: true });
        previousButton?.addEventListener("click", this.onArrowClick);
        nextButton?.addEventListener("click", this.onArrowClick);
      });

      window.addEventListener("resize", this.onResize);
      requestAnimationFrame(() => this.updateProgress());
    }

    disconnectedCallback() {
      this.querySelectorAll(".product-perfect-match__tab").forEach((button) => {
        button.removeEventListener("click", this.onTabClick);
      });

      this.tracks.forEach((track) => {
        const state = this.states.get(track);

        track.removeEventListener("pointerdown", this.onPointerDown);
        track.removeEventListener("pointermove", this.onPointerMove);
        track.removeEventListener("pointerup", this.onPointerUp);
        track.removeEventListener("pointercancel", this.onPointerUp);
        track.removeEventListener("click", this.onClick, true);
        track.removeEventListener("dragstart", this.onDragStart);
        track.removeEventListener("scroll", this.onScroll);
        state?.previousButton?.removeEventListener("click", this.onArrowClick);
        state?.nextButton?.removeEventListener("click", this.onArrowClick);
      });

      window.removeEventListener("resize", this.onResize);
    }

    onTabClick() {
      requestAnimationFrame(() => this.updateProgress());
      setTimeout(() => this.updateProgress(), 0);
    }

    onResize() {
      this.updateProgress();
    }

    onScroll(event) {
      this.updateTrackProgress(event.currentTarget);
    }

    onArrowClick(event) {
      const panel = event.currentTarget.closest(".product-perfect-match__panel");
      const track = panel?.querySelector(".product-perfect-match__grid");
      const firstItem = track?.querySelector(".product-perfect-match__item");

      if (!track || !firstItem) return;

      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = firstItem.getBoundingClientRect().width + gap;
      const direction = event.currentTarget.classList.contains(
        "product-perfect-match__arrow--previous",
      )
        ? -1
        : 1;
      const currentIndex = Math.round(track.scrollLeft / step);
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      track.scrollTo({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        left: (currentIndex + direction) * step,
      });
    }

    onPointerDown(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      const state = this.states.get(event.currentTarget);
      if (!state) return;

      state.dragging = true;
      state.hasMoved = false;
      state.suppressClick = false;
      state.startX = event.clientX;
      state.startScrollLeft = event.currentTarget.scrollLeft;
      state.activePointerId = event.pointerId;
    }

    onPointerMove(event) {
      const track = event.currentTarget;
      const state = this.states.get(track);
      if (!state || !state.dragging) return;

      const deltaX = event.clientX - state.startX;
      if (Math.abs(deltaX) > 4) {
        if (!state.hasMoved) {
          state.hasMoved = true;
          track.classList.add("is-dragging");

          try {
            track.setPointerCapture?.(event.pointerId);
          } catch (error) {
            // Pointer capture is optional; native scrolling still works without it.
          }
        }

        event.preventDefault();
      }

      track.scrollLeft = state.startScrollLeft - deltaX;
    }

    onPointerUp(event) {
      const track = event.currentTarget;
      const state = this.states.get(track);
      if (!state || !state.dragging) return;

      state.dragging = false;
      state.suppressClick = state.hasMoved;
      track.classList.remove("is-dragging");

      if (
        state.hasMoved &&
        state.activePointerId !== null &&
        track.hasPointerCapture?.(state.activePointerId)
      ) {
        try {
          track.releasePointerCapture(state.activePointerId);
        } catch (error) {
          // Ignore stale pointer capture state.
        }
      }

      state.activePointerId = null;
    }

    onClick(event) {
      const state = this.states.get(event.currentTarget);
      if (!state || !state.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      state.suppressClick = false;
    }

    onDragStart(event) {
      event.preventDefault();
    }

    updateProgress() {
      this.tracks.forEach((track) => this.updateTrackProgress(track));
    }

    updateTrackProgress(track) {
      const panel = track.closest(".product-perfect-match__panel");
      const navigation = panel?.querySelector(".product-perfect-match__navigation");
      const progress = panel?.querySelector(".product-perfect-match__progress");
      const thumb = progress?.querySelector(".product-perfect-match__progress-thumb");
      const state = this.states.get(track);

      if (!navigation || !progress || !thumb || !state) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      const hasOverflow = maxScroll > 1;

      navigation.hidden = !isMobile || !hasOverflow;
      state.previousButton.disabled = !hasOverflow || track.scrollLeft <= 1;
      state.nextButton.disabled = !hasOverflow || track.scrollLeft >= maxScroll - 1;

      if (!isMobile || !hasOverflow) return;

      const progressWidth = progress.clientWidth;
      if (progressWidth <= 0) return;

      const thumbWidth = Math.min(65, progressWidth);
      const scrollRatio = Math.max(0, Math.min(1, track.scrollLeft / maxScroll));
      const thumbOffset = (progressWidth - thumbWidth) * scrollRatio;

      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${thumbOffset}px)`;
    }
  }

  customElements.define("product-perfect-match", ProductPerfectMatch);
}
